import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";

import { useStatsSummary } from "@/features/matches";
import { cn } from "@/shared/lib";

import { DEATH_TOPICS } from "../../model/DEATH_TOPICS";
import type { DrillTopicView } from "../../model/Drill";
import { STATUS_LABELS } from "../../model/STATUS_LABELS";
import { TOPIC_LABELS } from "../../model/TOPIC_LABELS";

// Сколько нужно: верных ответов подряд — «понята», разборов без ошибки — «исправлена».
const UNDERSTOOD_STREAK = 2;
const FIXED_CLEAN_ANALYSES = 3;

const IN_DRILLS = msg({ message: "{streak} of {need} right in a row" });
const IN_MATCHES = msg({ message: "{clean} of {need} analyses without the mistake" });
const COMES_BACK = msg({ message: "Comes back {date}." });
const SURVIVAL = msg({
  message:
    "You die less than {score}% of players of your rank{previous, select, none {} other { · was {previous}%}}",
});

const STATUS_CHIP = {
  learning: cn("bg-sidebar-active text-muted"),
  understood: cn("bg-warn/15 text-warn"),
  fixed: cn("bg-good/15 text-good"),
} as const;

type TopicCardProps = { topic: DrillTopicView; now: Date };

// Тема игрока: состояние, полосы «в тренировке» и «в матчах», когда вернётся. У тем про
// смерти — ещё и выживаемость по быстрой оценке последних матчей.
export const TopicCard = ({ topic, now }: TopicCardProps) => {
  const { i18n, t } = useLingui();
  const stats = useStatsSummary();
  const fixed = topic.status === "fixed";
  const drillShare = fixed || topic.status === "understood" ? 1 : topic.streak / UNDERSTOOD_STREAK;
  const gameShare = fixed ? 1 : Math.min(1, topic.clean_analyses / FIXED_CLEAN_ANALYSES);
  const dueAt = topic.due_at === null ? null : new Date(topic.due_at);
  let note = t`In the next series.`;
  if (fixed) {
    note = t`No mistake in three analyses in a row: the topic left the drills.`;
  } else if (dueAt !== null && dueAt > now) {
    const date = new Intl.DateTimeFormat(i18n.locale, { day: "numeric", month: "long" }).format(
      dueAt,
    );
    note = i18n._({ ...COMES_BACK, values: { date } });
  }
  const survival = DEATH_TOPICS.has(topic.topic)
    ? stats.data?.summary?.axes.find((axis) => axis.axis === "survival")
    : undefined;
  const bar = (share: number, tone: string) => (
    <span className="block h-1.5 overflow-hidden rounded-full bg-sidebar-active">
      <span className={cn("block h-full", tone)} style={{ width: `${Math.round(share * 100)}%` }} />
    </span>
  );
  return (
    <article className="flex flex-col gap-2.5 rounded-xl border border-border bg-surface px-4.5 py-4">
      <div className="flex items-start justify-between gap-2.5">
        <h3 className="text-base font-semibold">{i18n._(TOPIC_LABELS[topic.topic])}</h3>
        <span
          className={cn(
            "shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold",
            STATUS_CHIP[topic.status],
          )}
        >
          {i18n._(STATUS_LABELS[topic.status])}
        </span>
      </div>
      <div className="flex flex-col gap-1.5 text-xs text-subtle">
        <span className="flex justify-between gap-2">
          <span>{t`In drills`}</span>
          <span>
            {i18n._({
              ...IN_DRILLS,
              values: {
                streak: Math.min(topic.streak, UNDERSTOOD_STREAK),
                need: UNDERSTOOD_STREAK,
              },
            })}
          </span>
        </span>
        {bar(drillShare, "bg-warn")}
        <span className="flex justify-between gap-2">
          <span>{t`In matches`}</span>
          <span>
            {i18n._({
              ...IN_MATCHES,
              values: {
                clean: Math.min(topic.clean_analyses, FIXED_CLEAN_ANALYSES),
                need: FIXED_CLEAN_ANALYSES,
              },
            })}
          </span>
        </span>
        {bar(gameShare, "bg-good")}
      </div>
      <span className="text-[13px] text-muted">{note}</span>
      {survival !== undefined && (
        <span className="text-[13px] text-muted">
          {i18n._({
            ...SURVIVAL,
            values: {
              score: Math.round(survival.score),
              previous: survival.previous === null ? "none" : Math.round(survival.previous),
            },
          })}
        </span>
      )}
    </article>
  );
};
